import type {GridInitialState} from '@mui/x-data-grid-pro';
import type {DataGridSettings} from 'services/settings/types';

export type DataGridSettingsOptions = {
  storeSize: boolean;
  storePage: boolean;
  storeSorting: boolean;
  storeFilter: boolean;
};

/** Grid state, or settings stored from it */
type DataGridState = Pick<GridInitialState, 'filter' | 'sorting'> & {
  columns?: {
    columnVisibilityModel?: NonNullable<
      GridInitialState['columns']
    >['columnVisibilityModel'];
    orderedFields?: string[];
    dimensions?: Record<string, {width?: number}>;
  };
  pagination?: {
    paginationModel?: NonNullable<
      GridInitialState['pagination']
    >['paginationModel'];
  };
};

type ColumnDimensions = NonNullable<
  NonNullable<DataGridSettings['columns']>['dimensions']
>;

const mapColumnWidths = (
  dimensions: NonNullable<DataGridState['columns']>['dimensions'],
): ColumnDimensions | undefined => {
  const widths = Object.entries(dimensions ?? {}).reduce<ColumnDimensions>(
    (acc, [field, {width}]) => {
      if (width !== undefined) {
        acc[field] = {width};
      }
      return acc;
    },
    {},
  );

  return Object.keys(widths).length > 0 ? widths : undefined;
};

// Date filter values are not serializable, the grid parses the string back
const mapFilterDates = (
  filter: GridInitialState['filter'],
): GridInitialState['filter'] =>
  filter?.filterModel
    ? {
        ...filter,
        filterModel: {
          ...filter.filterModel,
          items: filter.filterModel.items.map(item =>
            item.value instanceof Date
              ? {
                  ...item,
                  value: Number.isNaN(item.value.getTime())
                    ? undefined
                    : item.value.toISOString(),
                }
              : item,
          ),
        },
      }
    : filter;

/**
 * Dont keep the whole state, only the interesting parts.
 */
export const mapDataGridSettings = (
  {columns, filter, sorting, pagination}: DataGridState,
  {storeSize, storePage, storeSorting, storeFilter}: DataGridSettingsOptions,
): DataGridSettings => {
  const dimensions = mapColumnWidths(columns?.dimensions);
  const paginationModel = pagination?.paginationModel;

  return {
    columns:
      columns?.columnVisibilityModel || columns?.orderedFields || dimensions
        ? {
            columnVisibilityModel: columns?.columnVisibilityModel,
            orderedFields: columns?.orderedFields,
            dimensions,
          }
        : undefined,
    filter: storeFilter ? mapFilterDates(filter) : undefined,
    sorting: storeSorting ? sorting : undefined,
    // No undefined keys, they would override defaults when spread
    pagination:
      paginationModel && (storeSize || storePage)
        ? {
            paginationModel: {
              page: storePage ? (paginationModel.page ?? 0) : 0,
              ...(storeSize && paginationModel.pageSize !== undefined
                ? {pageSize: paginationModel.pageSize}
                : {}),
            },
          }
        : undefined,
  };
};
